import { isEqual } from "lodash";

import type { Workspace, WorkspaceConfig } from "@storyboard/web-client";

const workspaceConfigs = new Map<string, WorkspaceConfig>();
const listeners = new Set<() => void>();
let workspacesDeliveredFlag = false;

function notify() {
  for (const listener of listeners) listener();
}

/**
 * Records a workspace's compiled config and notifies subscribers when it changed.
 *
 * The config lives here rather than on the store's `workspace` row, so a config-only change upserts an unchanged row
 * and the store notifies no one; this notification is the only signal a reader gets. An equal config keeps the stored
 * object, so a snapshot read through {@link subscribeWorkspaceConfigs} stays stable across refetches.
 */
export function updateWorkspaceConfig(workspace: Workspace) {
  if (!workspace.config || isEqual(workspaceConfigs.get(workspace.id), workspace.config)) {
    return;
  }
  workspaceConfigs.set(workspace.id, workspace.config);
  notify();
}

export function getWorkspaceConfig(workspaceId: string): WorkspaceConfig | undefined {
  return workspaceConfigs.get(workspaceId);
}

/**
 * Records that the API has delivered the user's workspaces, and with them every config this session will see.
 *
 * Notifies subscribers the first time, since a gate reading "loading" becomes "enabled" or "disabled".
 */
export function markWorkspacesDelivered() {
  if (workspacesDeliveredFlag) return;
  workspacesDeliveredFlag = true;
  notify();
}

/**
 * Whether the API has delivered the user's workspaces this session.
 *
 * A snapshot-restored workspace row carries no config, so until this is true an absent config means "not loaded yet",
 * not "off". Once it is true, a workspace with no config, or one the user does not belong to, has none coming.
 */
export function workspacesDelivered(): boolean {
  return workspacesDeliveredFlag;
}

/** Subscribes to workspace config changes, in the shape `useSyncExternalStore` takes; returns the unsubscribe. */
export function subscribeWorkspaceConfigs(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function handsFreeEnabledForWorkspace(workspaceId: string) {
  const config = getWorkspaceConfig(workspaceId);
  return config?.handsFree?.status === "all";
}

export function getWorkspaceLanguages(workspaceId: string) {
  const workspaceConfig = getWorkspaceConfig(workspaceId);
  if (!workspaceConfig) return [];
  return workspaceConfig.allowedLanguages ?? [];
}
